/* How a column of turns becomes a column of squares, and what it is worth.
 *
 * These are the rotation's arithmetic, and they were the part of the store
 * hardest to be sure about: half turns pair with what follows them, zero-turn
 * services leave the grid when they finish, skipped turns are woven back in at
 * the point they happened, and every one of those rules exists because getting
 * it wrong hands somebody else's turn to the wrong technician.
 *
 * They live here rather than in store.js because they are already pure — plain
 * functions over plain arrays, no reactivity, no Supabase, no clock. Nothing
 * about them needed changing to be tested; they only needed somewhere to be
 * imported from. store.js wraps each one in the computed that feeds it live
 * data, and that wrapper is the only part that knows what a `ref` is.
 *
 * A turn, as far as this file is concerned, is `{ turnWeight, endedAt }`. Every
 * other field on it belongs to whoever is drawing the square.
 */

/* A half turn takes half a square, drawn as a fraction. Once a square has been
 * opened by a half turn, the next turn joins it whatever its weight — so the
 * square's value is simply the sum of what is in it:
 *
 *   [H, H]  -> one square, 1 turn
 *   [H, M]  -> one square, 1.5 turns (a square that started as a half can be
 *              worth more than one)
 *   [.., H] -> trailing half: square still open, counts 0.5 for now, and its
 *              owner jumps the queue until it is finished
 *
 * Only the turn immediately after a half joins it, so two halves with a full
 * turn between them never combine — [H, M, H] is 1.5 + 0.5, not 1 + 1.
 *
 * A ZERO-turn service (a nail fix) takes a square of its own and is worth
 * nothing. It never pairs with an open half: filling somebody's half turn with
 * work that does not count would close the square at 0.5 and quietly take away
 * the other half they are owed.
 */
export function buildSlots(all) {
  // A zero-turn service is not a turn: it holds a square while it is running,
  // so the desk can see it and stop it, and leaves no trace on the grid when
  // it ends. The tech's own square is theirs again and the numbering below is
  // untouched. It is still on tt_turns, so History has it.
  var list = all.filter(function (t) { return t.turnWeight !== 0 || t.endedAt == null; });
  var slots = [];
  var i = 0;
  while (i < list.length) {
    var t = list[i];
    if (t.turnWeight !== 0.5) {
      slots.push({ half: false, open: false, zero: t.turnWeight === 0,
                   credit: t.turnWeight, turns: [t] });
      i += 1;
      continue;
    }
    var next = list[i + 1];
    if (next && next.turnWeight > 0) {
      slots.push({
        half: true, open: false,
        credit: t.turnWeight + next.turnWeight,
        turns: [t, next]
      });
      i += 2;
      continue;
    }
    slots.push({ half: true, open: true, credit: t.turnWeight, turns: [t] });
    i += 1;
  }
  return slots;
}

/* A column is its slots with the turns it was bypassed for woven back in at
 * the point they happened, so a break in the middle of the day draws its red
 * squares in the middle of the column rather than at the bottom. Each entry in
 * `skips` is the number of squares taken when that turn went past. */
export function buildRows(slots, skips) {
  var out = [];
  var marks = skips.slice().sort(function (a, b) { return a - b; });
  var m = 0;
  for (var i = 0; i < slots.length; i++) {
    while (m < marks.length && marks[m] <= i) { out.push({ skip: true }); m += 1; }
    out.push(slots[i]);
  }
  while (m < marks.length) { out.push({ skip: true }); m += 1; }
  return out;
}

/* What a column is worth, and whether it is holding a half turn open.
 *
 * A skipped turn costs the same as a missed one, so stepping out for an hour
 * does not come back as a licence to take the next four turns in a row.
 *
 * `openHalf` is a trailing, unpaired half turn — its owner is next for any
 * service, because they are owed the other half.
 */
export function creditFrom(slots, missedTurns, skipCount) {
  var credit = missedTurns + skipCount;
  var openHalf = null;
  slots.forEach(function (s) {
    credit += s.credit;
    if (s.open) openHalf = s.turns[0];
  });
  return { credit: credit, openHalf: openHalf };
}
